export type IAudioRejectionReason = 'unsupported-type' | 'too-large' | 'upload-failed';

export const getExtensionFromFileName = (fileName: string): string | undefined => {
    const match = /\.([a-z0-9]+)$/i.exec(fileName);
    return match ? match[1].toLowerCase() : undefined;
};

// Matches the attachment cap in NoteForm. The server transcription pipeline chunks anything
// over 24 MB / 1400 s, so 50 MB is safe to accept here.
export const MAX_AUDIO_FILE_BYTES = 50 * 1024 * 1024;

// Accept by MIME OR extension — native file pickers frequently report an empty or
// 'application/octet-stream' MIME type, so a missing/unknown MIME must not be a hard reject.
// These mirror what OpenAI's transcription API accepts (flac, mp3, mp4, mpeg, mpga, m4a, ogg, wav,
// webm). Notably raw .aac is NOT supported by the model (and the server doesn't transcode small
// files), so it's excluded — .m4a still works since that's AAC inside an MP4 container.
const ACCEPTED_MIME_TYPES = [
    'audio/mpeg', 'audio/mp3', 'audio/mp4', 'audio/x-m4a',
    'audio/wav', 'audio/x-wav', 'audio/webm', 'audio/ogg', 'audio/flac', 'audio/x-flac'
];

const ACCEPTED_EXTENSIONS = ['mp3', 'm4a', 'wav', 'webm', 'ogg', 'oga', 'flac'];

const EXTENSION_TO_MIME: Record<string, string> = {
    mp3: 'audio/mpeg', m4a: 'audio/mp4', wav: 'audio/wav',
    webm: 'audio/webm', ogg: 'audio/ogg', oga: 'audio/ogg', flac: 'audio/flac'
};

// Native pickers often report an empty MIME (we accept those by extension). RecordingService
// derives the stored filename's extension from the MIME, so without a concrete type the extension
// is lost (filename ends in a bare ".") and server transcription can fail to pick a decoder.
// Resolve a real MIME from the extension when the file doesn't carry one.
export const resolveAudioMimeType = (file: File): string => {
    if (file.type) {
        return file.type;
    }
    const extension = getExtensionFromFileName(file.name);
    return (extension && EXTENSION_TO_MIME[extension]) || '';
};

// Stricter than resolveAudioMimeType, for a server that accepts only ACCEPTED_MIME_TYPES: pickers
// often report application/octet-stream, so a type outside the list falls back to the extension.
export const resolveAcceptedAudioMimeType = (file: File): string | undefined => {
    const reported = file.type.toLowerCase();
    if (ACCEPTED_MIME_TYPES.includes(reported)) {
        return reported;
    }
    const extension = getExtensionFromFileName(file.name);
    return extension ? EXTENSION_TO_MIME[extension] : undefined;
};

// .mp4 shares the MPEG-4 container with .m4a, so a video .mp4 often reports MIME 'audio/mp4'
// and would otherwise slip through to a transcription that fails on the video stream. Block it
// (and any video/* MIME) by extension up front so the user gets a clean "unsupported" message.
const BLOCKED_EXTENSIONS = ['mp4'];

// Pure-audio extensions used for the file input's `accept`. We deliberately list extensions (not
// `audio/*`) so `.mp4` is grayed out (audio/* matches mp4, and MIME can't exclude it without also
// excluding .m4a — same container). We also omit the video-capable containers (.webm/.ogg) here:
// iOS offers "Photo Library / Take Video" in the file sheet when `accept` matches a video UTI, and
// those are the only formats that do. They're still honored by validateAudioFile if a user reaches
// one via the OS "All Files" override.
const PICKER_EXTENSIONS = ['mp3', 'm4a', 'wav', 'flac'];
export const AUDIO_UPLOAD_ACCEPT = PICKER_EXTENSIONS.map(ext => `.${ext}`).join(',');

export const isAcceptedAudioFile = (file: File): boolean => {
    const extension = getExtensionFromFileName(file.name);
    if (extension && BLOCKED_EXTENSIONS.includes(extension)) {
        return false;
    }
    if (file.type && file.type.toLowerCase().startsWith('video/')) {
        return false;
    }
    if (file.type && ACCEPTED_MIME_TYPES.includes(file.type.toLowerCase())) {
        return true;
    }
    return Boolean(extension && ACCEPTED_EXTENSIONS.includes(extension));
};

// The rejection reason, or undefined when the file is acceptable. A rejected file is an ordinary
// outcome here rather than an error: uploads are validated a batch at a time and the user is told
// which files were turned away and why, so the caller needs the reason in hand to keep going rather
// than an exception that would abandon the rest of the batch. Returning the reason alone, rather
// than pairing it with an ok flag, keeps the two from ever disagreeing.
export const validateAudioFile = (file: File): IAudioRejectionReason | undefined => {
    if (!isAcceptedAudioFile(file)) {
        return 'unsupported-type';
    }
    if (file.size > MAX_AUDIO_FILE_BYTES) {
        return 'too-large';
    }
    return undefined;
};
